import React, { useState, useRef } from 'react';
import { Send, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const MANDATE_OPTIONS = [
  { value: 'senior_debt', label: 'Senior Debt' },
  { value: 'mezzanine', label: 'Mezzanine' },
  { value: 'preferred_equity', label: 'Preferred Equity' },
  { value: 'common_equity', label: 'Common Equity' },
  { value: 'bridge', label: 'Bridge' },
  { value: 'construction', label: 'Construction' },
  { value: 'special_situations', label: 'Special Situations' },
  { value: 'private_credit', label: 'Private Credit (multi-strategy)' },
  { value: 'family_office_general', label: 'Family Office — General Mandate' },
  { value: 'other', label: 'Other' },
];

export const RequestAccessModal = ({ open, onClose }) => {
  const [form, setForm] = useState({ name: '', firm: '', email: '', mandateType: '', message: '' });
  const [state, setState] = useState({ submitting: false, success: false, error: null });

  const busy = useRef(false);
  if (!open) return null;

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy.current) return;
    busy.current = true;
    setState({ submitting: true, success: false, error: null });
    try {
      const res = await fetch(`${API_URL}/api/public/lender-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          sourcePage: typeof window !== 'undefined' ? window.location.pathname : '/for-lenders',
        }),
      });
      if (!res.ok) throw new Error('Submission could not be confirmed. Please try again.');
      setState({ submitting: false, success: true, error: null });
    } catch (_) {
      setState({ submitting: false, success: false, error: 'Submission could not be confirmed. Please try again.' });
    } finally { busy.current = false; }
  };

  const reset = () => {
    setForm({ name: '', firm: '', email: '', mandateType: '', message: '' });
    setState({ submitting: false, success: false, error: null });
    onClose();
  };

  const valid = form.name.trim() && form.firm.trim() && form.email.trim() && form.mandateType;

  return (
    <Dialog open={open} onOpenChange={value=>{if(!value)reset();}}><DialogContent className="max-h-[90vh] overflow-y-auto p-0" data-testid="request-access-modal"><DialogTitle className="sr-only">Lender and family office access request</DialogTitle><DialogDescription className="sr-only">Provide your firm and mandate details for human review.</DialogDescription>
        {state.success ? (
          <div className="p-8 text-center" data-testid="request-access-success">
            <div className="w-14 h-14 mx-auto rounded-full bg-teal/10 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-7 h-7 text-teal" />
            </div>
            <h3 className="font-grotesk text-xl font-semibold text-foreground mb-2">Request received</h3>
            <p className="font-inter text-sm text-muted-foreground mb-6">
              Thank you. Your request has been received and will be reviewed by the GP Capital team.
            </p>
            <Button className="bg-teal hover:bg-teal-dark text-white" onClick={reset} data-testid="request-access-done">
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 sm:p-8" data-testid="request-access-form">
            <p className="font-inter text-[11px] uppercase tracking-wider text-teal mb-2">Request Access</p>
            <h3 className="font-grotesk text-xl sm:text-2xl font-semibold text-foreground mb-2">
              Lender &amp; Family Office onboarding
            </h3>
            <p className="font-inter text-sm text-muted-foreground mb-6">
              Tell us about your mandate. We will follow up to discuss eligibility and next steps. No automated deal feeds — every access decision is made by GP Capital.
            </p>

            <div className="space-y-4">
              <div>
                <Label htmlFor="ral-name" className="text-xs text-muted-foreground">Full name</Label>
                <Input
                  id="ral-name" maxLength={120}
                  value={form.name}
                  onChange={update('name')}
                  required
                  autoComplete="name"
                  placeholder="Your name"
                  className="mt-1"
                  data-testid="request-access-name"
                />
              </div>

              <div>
                <Label htmlFor="ral-firm" className="text-xs text-muted-foreground">Firm / Family office</Label>
                <Input
                  id="ral-firm" maxLength={160}
                  value={form.firm}
                  onChange={update('firm')}
                  required
                  autoComplete="organization"
                  placeholder="e.g. Coastal Credit Partners"
                  className="mt-1"
                  data-testid="request-access-firm"
                />
              </div>

              <div>
                <Label htmlFor="ral-email" className="text-xs text-muted-foreground">Work email</Label>
                <Input
                  id="ral-email" maxLength={254}
                  type="email"
                  value={form.email}
                  onChange={update('email')}
                  required
                  autoComplete="email"
                  placeholder="name@firm.com"
                  className="mt-1"
                  data-testid="request-access-email"
                />
              </div>

              <div>
                <Label htmlFor="ral-mandate" className="text-xs text-muted-foreground">Mandate type</Label>
                <select
                  id="ral-mandate"
                  value={form.mandateType}
                  onChange={update('mandateType')}
                  required
                  className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal/40"
                  data-testid="request-access-mandate"
                >
                  <option value="" disabled>Select your primary mandate…</option>
                  {MANDATE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="ral-msg" className="text-xs text-muted-foreground">
                  Context <span className="text-muted-foreground/60">(optional)</span>
                </Label>
                <Textarea
                  id="ral-msg"
                  value={form.message}
                  onChange={update('message')}
                  rows={3}
                  maxLength={2000}
                  placeholder="Ticket size, geography, product focus…"
                  className="mt-1 resize-none"
                  data-testid="request-access-message"
                />
              </div>
            </div>

            {state.error && (
              <div className="mt-4 flex items-start gap-2 p-3 rounded-md bg-red-50 text-red-700 text-xs" role="alert" data-testid="request-access-error">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{state.error}</span>
              </div>
            )}

            <div className="mt-6 flex items-center justify-between gap-3">
              <p className="text-[11px] text-muted-foreground/70">
                By submitting you agree to be contacted about institutional opportunities.
              </p>
              <Button
                type="submit"
                disabled={!valid || state.submitting}
                className="bg-teal hover:bg-teal-dark text-white"
                data-testid="request-access-submit"
              >
                {state.submitting ? 'Sending…' : (<><Send className="w-4 h-4 mr-2" /> Submit</>)}
              </Button>
            </div>
          </form>
        )}
    </DialogContent></Dialog>
  );
};

export default RequestAccessModal;
